'use strict'

/**
 * Catalog material items on a master-lot manifest.
 *
 * Each item is a Product/SKU contribution keyed by sku×location, with a
 * location snapshot and quantity that contributes weight/purity to the lot.
 */

import {prefixedId} from '@/util/prefixedId'
import {UNIT_KINDS} from '../constants'
import {catalogUnitWeightTroyOunces, richWeightFromTroyOunces} from './manifestWeights'
import {normalizeProductMetalKey} from './display'
import {
  catalogPurityFractionFromProduct,
  catalogUnitWeightFromProduct,
} from './catalogProductDetails'

const positiveWholeNumber = (value) => {
  const quantity = Number(value)
  return Number.isInteger(quantity) && quantity > 0 ? quantity : null
}

const inventoryLimit = (entry) => {
  const onHand = entry?.locationSnapshot?.qtyOnHand
  if (onHand === null || onHand === undefined) return null
  const quantity = Math.floor(Number(onHand))
  return Number.isFinite(quantity) && quantity >= 0 ? quantity : null
}

export const catalogItemKey = (entry) => {
  if (!entry?.sku) return null
  const sku = String(entry.sku).trim().toUpperCase()
  const locationCode = String(entry?.locationSnapshot?.locationCode || '').trim().toUpperCase()
  return `${sku}::${locationCode}`
}

/** @returns {number|null} positive whole qty for catalog lines; `1` for non-catalog; null if corrupt */
export const catalogItemQuantity = (entry) => {
  if (entry?.kind !== UNIT_KINDS.CATALOG) return 1
  return positiveWholeNumber(entry.quantity)
}

export const catalogUnitWeightToz = (entry) => {
  if (entry?.unitWeight !== null && entry?.unitWeight !== undefined) {
    return catalogUnitWeightTroyOunces(entry) ?? 0
  }
  const quantity = catalogItemQuantity(entry)
  if (!quantity) return 0
  const total = Number(entry?.weightToz)
  return Number.isFinite(total) ? total / quantity : 0
}

export const hasValidCatalogUnitWeight = (entry) => catalogUnitWeightToz(entry) > 0

const validPurityFraction = (value) => {
  const fraction = Number(value)
  return Number.isFinite(fraction) && fraction > 0 && fraction <= 1 ? fraction : null
}

export const catalogPurityFraction = (entry, metalKey = entry?.metalKey) => {
  const explicit = validPurityFraction(entry?.purityFraction)
  if (explicit !== null) return explicit
  const percent = Number(entry?.purity?.[metalKey])
  return Number.isFinite(percent) && percent > 0 && percent <= 100 ? percent / 100 : null
}

export const hasValidCatalogPurity = (entry) => catalogPurityFraction(entry) !== null

export const withCatalogUnitWeight = (entry, requestedUnitWeightToz, weightSource = 'manual') => {
  const unitWeightToz = Number(requestedUnitWeightToz)
  if (!Number.isFinite(unitWeightToz) || unitWeightToz <= 0) {
    throw new Error('catalog item unit weight must be positive')
  }
  const quantity = catalogItemQuantity(entry)
  if (quantity === null) {
    throw new Error('catalog item quantity must be a positive whole number')
  }
  return {
    ...entry,
    unitWeight: richWeightFromTroyOunces(unitWeightToz),
    weightToz: unitWeightToz * quantity,
    weightSource,
  }
}

export const withCatalogPurity = (
  entry,
  {metalKey = entry?.metalKey, purityFraction, puritySource = 'manual'},
) => {
  const fraction = validPurityFraction(purityFraction)
  if (!metalKey) {
    throw new Error('catalog item purity requires a metal key')
  }
  if (fraction === null) {
    throw new Error('catalog item purity fraction must be between 0 and 1')
  }
  return {
    ...entry,
    metalKey,
    purity: {...entry.purity, [metalKey]: fraction * 100},
    purityFraction: fraction,
    puritySource,
  }
}

export const withCatalogQuantity = (entry, requestedQuantity) => {
  const quantity = positiveWholeNumber(requestedQuantity)
  if (quantity === null) {
    throw new Error('catalog item quantity must be a positive whole number')
  }
  const limit = inventoryLimit(entry)
  if (limit !== null && quantity > limit) {
    throw new Error(`catalog item quantity ${quantity} exceeds on-hand ${limit}`)
  }
  const unitWeightToz = catalogUnitWeightToz(entry)
  return {
    ...entry,
    quantity,
    unitWeight: richWeightFromTroyOunces(unitWeightToz),
    weightToz: unitWeightToz * quantity,
  }
}

export const addCatalogItem = (entries, entry) => {
  const currentEntries = Array.isArray(entries) ? entries : []
  if (entry?.kind !== UNIT_KINDS.CATALOG) return [...currentEntries, entry]

  const key = catalogItemKey(entry)
  const index = currentEntries.findIndex((candidate) => catalogItemKey(candidate) === key)
  if (index < 0) return [...currentEntries, withCatalogQuantity(entry, entry.quantity)]

  const current = currentEntries[index]
  const currentQty = catalogItemQuantity(current)
  if (currentQty === null) {
    throw new Error('catalog item quantity must be a positive whole number')
  }
  const nextQty = currentQty + 1
  const limit = inventoryLimit(current)
  if (limit !== null && nextQty > limit) {
    throw new Error(`catalog item quantity ${nextQty} exceeds on-hand ${limit}`)
  }

  const next = [...currentEntries]
  next[index] = withCatalogQuantity(current, nextQty)
  return next
}

/**
 * Set quantity for a catalog item (sku×location). Rejects at this boundary when
 * unit weight or purity is missing/invalid — callers must not treat no-op as success.
 * @returns {object[]} next entries
 * @throws {Error} when the item cannot accept a quantity yet
 */
export const setCatalogItemQuantity = (entries, entry, requestedQuantity) => {
  const currentEntries = Array.isArray(entries) ? entries : []
  if (entry?.kind !== UNIT_KINDS.CATALOG) {
    throw new Error('setCatalogItemQuantity requires a catalog item')
  }
  if (!hasValidCatalogUnitWeight(entry)) {
    throw new Error('catalog item requires a positive unit weight before quantity can be set')
  }
  if (!hasValidCatalogPurity(entry)) {
    throw new Error('catalog item requires a valid purity before quantity can be set')
  }
  const quantity = positiveWholeNumber(requestedQuantity)
  if (quantity === null) {
    throw new Error('catalog item quantity must be a positive whole number')
  }

  const key = catalogItemKey(entry)
  const index = currentEntries.findIndex((candidate) => catalogItemKey(candidate) === key)
  if (index < 0) return [...currentEntries, withCatalogQuantity(entry, quantity)]

  const next = [...currentEntries]
  next[index] = withCatalogQuantity(currentEntries[index], quantity)
  return next
}

export const changeCatalogItemQuantity = (entries, {id, delta, quantity}) => {
  const currentEntries = Array.isArray(entries) ? entries : []
  const index = currentEntries.findIndex((entry) => entry?.id === id)
  if (index < 0) {
    throw new Error('catalog item not found')
  }

  const entry = currentEntries[index]
  const currentQty = catalogItemQuantity(entry)
  const requested = Number.isFinite(Number(quantity))
    ? Number(quantity)
    : (currentQty === null ? NaN : currentQty + Number(delta || 0))

  const next = [...currentEntries]
  next[index] = withCatalogQuantity(entry, requested)
  return next
}

export const changeCatalogItemUnitWeight = (entries, {id, unitWeightToz}) => {
  const currentEntries = Array.isArray(entries) ? entries : []
  const index = currentEntries.findIndex((entry) => entry?.id === id)
  if (index < 0) {
    throw new Error('catalog item not found')
  }

  const next = [...currentEntries]
  next[index] = withCatalogUnitWeight(currentEntries[index], unitWeightToz)
  return next
}

export const changeCatalogItemPurity = (entries, {id, metalKey, purityFraction}) => {
  const currentEntries = Array.isArray(entries) ? entries : []
  const index = currentEntries.findIndex((entry) => entry?.id === id)
  if (index < 0) {
    throw new Error('catalog item not found')
  }

  const next = [...currentEntries]
  next[index] = withCatalogPurity(currentEntries[index], {metalKey, purityFraction})
  return next
}

export const hasInvalidCatalogUnitWeights = (entries) => (
  (Array.isArray(entries) ? entries : [])
    .some((entry) => entry?.kind === UNIT_KINDS.CATALOG && !hasValidCatalogUnitWeight(entry))
)

export const hasInvalidCatalogPurities = (entries) => (
  (Array.isArray(entries) ? entries : [])
    .some((entry) => entry?.kind === UNIT_KINDS.CATALOG && !hasValidCatalogPurity(entry))
)

export const removeCatalogItem = (entries, selection) => {
  const key = catalogItemKey({
    sku: selection?.sku,
    locationSnapshot: {locationCode: selection?.locationCode},
  })
  return (Array.isArray(entries) ? entries : [])
    .filter((entry) => catalogItemKey(entry) !== key)
}

export const findCatalogItem = (entries, {sku, locationCode}) => {
  const key = catalogItemKey({sku, locationSnapshot: {locationCode}})
  return (Array.isArray(entries) ? entries : [])
    .find((candidate) => catalogItemKey(candidate) === key) || null
}

export const quantityForCatalogItem = (entries, {sku, locationCode}) => {
  const entry = findCatalogItem(entries, {sku, locationCode})
  if (!entry) return 0
  return catalogItemQuantity(entry) ?? 0
}

/** Read catalog lines from the canonical persisted manifest shape. */
export function catalogEntriesFromManifest(manifest) {
  if (!manifest || typeof manifest !== 'object') return []
  return Array.isArray(manifest.catalogEntries) ? manifest.catalogEntries : []
}

const pickAggregateLocation = (product) => {
  const byLoc = product?.onHandByLocation
  if (Array.isArray(byLoc) && byLoc.length > 0) {
    return byLoc.find((loc) => loc.qtyOnHand > 0) || byLoc[0]
  }
  // Live Product has aggregate qty only until per-location read exists.
  const qty = Number(product?.qtyOnHand ?? product?.qtyAvailable)
  return {
    locationCode: 'AGG',
    qtyOnHand: Number.isFinite(qty) ? qty : null,
  }
}

/**
 * Build a Product-shaped catalog manifest line from a product + location snapshot.
 * @param {object} product
 * @param {{ locationId?: string, locationCode?: string, netsuiteInternalId?: string, qtyOnHand?: number }|null} [location]
 * @param {{ unitWeightToz?: number, weightSource?: string, purityFraction?: number, puritySource?: string, id?: string }} [options]
 */
export function buildCatalogManifestLine(product, location = null, options = {}) {
  if (!product?.sku) {
    throw new Error('catalog line requires product.sku')
  }
  if (!product.netsuite_internalId) {
    throw new Error('catalog line requires product.netsuite_internalId')
  }
  const metal = normalizeProductMetalKey(product)
  const catalogWeightToz = catalogUnitWeightFromProduct(product)
  const overrideWeightToz = Number(options.unitWeightToz)
  const hasOverride = Number.isFinite(overrideWeightToz) && overrideWeightToz > 0
  const unitWeightToz = hasOverride ? overrideWeightToz : catalogWeightToz
  const weightSource = hasOverride
    ? options.weightSource || 'manual'
    : (catalogWeightToz !== null ? 'catalog' : null)
  const catalogPurity = catalogPurityFractionFromProduct(product, metal)
  const overridePurityFraction = Number(options.purityFraction)
  const hasPurityOverride = Number.isFinite(overridePurityFraction)
    && overridePurityFraction > 0
    && overridePurityFraction <= 1
  const purityFraction = hasPurityOverride
    ? overridePurityFraction
    : catalogPurity
  const puritySource = hasPurityOverride
    ? options.puritySource || 'manual'
    : (purityFraction !== null ? 'catalog' : null)
  const loc = location || pickAggregateLocation(product)
  if (!loc?.locationCode) {
    throw new Error('catalog line requires locationSnapshot.locationCode')
  }
  return {
    id: options.id || prefixedId('catalog'),
    sku: product.sku,
    productNetsuiteInternalId: String(product.netsuite_internalId),
    name: product.name || product.sku,
    // Keep description for legacy mock validators / older manifest rows.
    description: product.name || product.sku,
    kind: UNIT_KINDS.CATALOG,
    quantity: 1,
    unitWeight: richWeightFromTroyOunces(unitWeightToz),
    weightToz: unitWeightToz,
    weightSource,
    metalKey: metal,
    purity: metal && purityFraction !== null ? {[metal]: purityFraction * 100} : {},
    purityFraction,
    puritySource,
    locationSnapshot: {
      locationId: loc.locationId || null,
      locationCode: loc.locationCode,
      netsuiteInternalId: loc.netsuiteInternalId,
      qtyOnHand: Number.isFinite(loc.qtyOnHand) ? loc.qtyOnHand : null,
    },
  }
}
